import { Component, inject, signal, HostListener } from '@angular/core';

import { Router } from '@angular/router';
import { UserMenuComponent } from '@sandbox-platform/shared-ui';
import { AuthStore } from '../store/auth.store';

@Component({
  standalone: true,
  imports: [UserMenuComponent],
  selector: 'lib-portal-header',
  template: `
    <header
      class="bg-gray-900 shadow-lg border-b border-gray-700 w-full relative z-50 text-white"
    >
      <div class="px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo/Brand -->
          <div class="flex items-center gap-x-4">
            <img
              src="./banner.jpg"
              alt="SNDBX OS"
              class="h-10 w-auto mr-3 hidden sm:block animate-fade-in"
            />

            <h1 class="text-lg font-semibold text-white hidden sm:block">
              SNDBX OS
            </h1>
          </div>

          <!-- Right side: Mobile menu button + User Menu -->
          <div class="flex items-center space-x-4">
            <!-- Mobile menu button -->
            <button
              type="button"
              (click)="toggleMobileMenu()"
              class="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-200"
              aria-controls="mobile-menu"
              [attr.aria-expanded]="isMobileMenuOpen()"
            >
              <span class="sr-only">{{
                isMobileMenuOpen() ? 'Close main menu' : 'Open main menu'
              }}</span>
              <!-- Hamburger/Close icon with rotation animation -->
              <div class="relative w-6 h-6">
                <div
                  class="absolute inset-0 transform transition-transform duration-300"
                  [class.rotate-180]="isMobileMenuOpen()"
                >
                  @if (!isMobileMenuOpen()) {
                  <svg
                    class="block h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                    />
                  </svg>
                  } @else {
                  <svg
                    class="block h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  }
                </div>
              </div>
            </button>

            <!-- User Menu - Hidden on mobile -->
            <div class="hidden md:block">
              <lib-user-menu
                [userFullName]="authStore.userFullName()"
                [userInitials]="getUserInitials()"
                [showInitials]="true"
                [profileHandler]="goToProfileHandler"
                [signOutHandler]="signOutHandler"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile backdrop overlay -->
      @if (isMobileMenuOpen()) {
      <div
        class="fixed inset-0 bg-gray-600 bg-opacity-50 z-30 md:hidden transition-opacity duration-300"
        (click)="closeMobileMenu()"
        [style.animation]="'fadeIn 0.3s ease-out'"
      ></div>
      }

      <!-- Mobile menu -->
      <div class="md:hidden relative z-40">
        <div
          class="overflow-hidden transition-all duration-300 ease-out"
          [style.max-height]="isMobileMenuOpen() ? '500px' : '0px'"
        >
          <div class="bg-gray-800 border-t border-gray-700 shadow-lg">
            <!-- User Profile Section -->
            <div class="px-4 py-3 border-b border-gray-700">
              <div class="flex items-center space-x-3">
                <!-- User Avatar -->
                <div
                  class="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-medium shadow-md"
                >
                  {{ getUserInitials() }}
                </div>
                <!-- User Info -->
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-white truncate">
                    {{ authStore.userFullName() }}
                  </p>
                  <p class="text-xs text-gray-300 truncate">Portal User</p>
                </div>
              </div>
            </div>

            <!-- User Actions Section -->
            <div class="border-t border-gray-700 pt-2 pb-3">
              <button
                (click)="goToProfile()"
                class="w-full text-left px-4 py-3 text-sm text-gray-200 hover:bg-gray-700 hover:text-white transition-colors duration-200 focus:outline-none focus:bg-gray-700"
              >
                <div class="flex items-center">
                  <svg
                    class="w-5 h-5 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Profile
                </div>
              </button>
              <button
                (click)="signOut()"
                class="w-full text-left px-4 py-3 text-sm text-gray-200 hover:bg-gray-700 hover:text-red-400 transition-colors duration-200 focus:outline-none focus:bg-gray-700"
              >
                <div class="flex items-center">
                  <svg
                    class="w-5 h-5 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Sign out
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  `,
  styles: [
    `
      .animate-fade-in {
        opacity: 0;
        animation: fadeIn 0.8s ease-in-out forwards;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
    `,
  ],
})
export class PortalHeaderComponent {
  private router = inject(Router);
  public authStore = inject(AuthStore);

  isMobileMenuOpen = signal(false);

  toggleMobileMenu() {
    this.isMobileMenuOpen.update((value) => !value);
  }

  closeMobileMenu() {
    this.isMobileMenuOpen.set(false);
  }

  readonly goToProfileHandler = () => {
    this.router.navigate(['/profile']);
  };

  readonly signOutHandler = async () => {
    await this.authStore.logout();
    window.location.href = '/auth/login';
  };

  // Close menu when clicking outside (escape key)
  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isMobileMenuOpen()) {
      this.closeMobileMenu();
    }
  }

  // Close menu on window resize to desktop size
  @HostListener('window:resize')
  onResize() {
    if (window.innerWidth >= 768 && this.isMobileMenuOpen()) {
      this.closeMobileMenu();
    }
  }

  // Get user initials for avatar
  getUserInitials(): string {
    const user = this.authStore.user();
    if (!user) return 'U';

    const firstInitial = user.first_name?.charAt(0).toUpperCase() || '';
    const lastInitial = user.last_name?.charAt(0).toUpperCase() || '';

    return firstInitial + lastInitial || 'U';
  }

  goToProfile(): void {
    this.closeMobileMenu();
    this.router.navigate(['/profile']);
  }

  async signOut(): Promise<void> {
    this.closeMobileMenu();
    await this.authStore.logout();
    window.location.href = '/auth/login';
  }
}
